import React from "react";
import { cn } from "../lib/utils";
import { AppleGlyph, GooglePlayGlyph } from "./BrandGlyphs";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  DOWNLOAD_URL,
  TRUST_TRIO,
} from "../lib/appLinks";
import { trackStoreClick } from "../lib/storeClicks";

// Scan-to-download block: App Store + Google Play badges, an optional QR card,
// and an optional honest trust trio. Borrowed from peanut-app.io's pattern of
// pairing a QR + store badges + a trust line next to every CTA (thread LL-0136).
// On-brand editorial styling (ink/border/canvas), not the official store PNGs,
// but carrying the REAL Apple and Google Play marks (see BrandGlyphs.tsx).
//
// The store URLs, QR target/image and badge labels default to the production app
// links, and can be overridden (e.g. the beta page passes TestFlight + Play Beta
// links and the beta QR; thread LL-0141).

function StoreBadge({
  href,
  glyph,
  topLine,
  bottomLine,
  tone = "dark",
  placement,
}: {
  href: string;
  placement: string;
  glyph: React.ReactNode;
  topLine: string;
  bottomLine: string;
  tone?: "dark" | "light";
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackStoreClick(href, placement)}
      // `light` is for badges sitting on a saturated ground, where the default
      // dark badge reads as the dull grey block the rest of the page avoids.
      // Both are permitted by the Apple and Google badge guidelines.
      className={cn(
        "inline-flex items-center gap-3 h-14 px-5 rounded-2xl border-2 active:translate-y-0.5 transition-all duration-200",
        tone === "light"
          ? "bg-[#f9f9f7] text-[#2d3849] border-[#f9f9f7] shadow-[4px_4px_0_0_rgba(11,58,56,0.55)] hover:shadow-[2px_2px_0_0_rgba(11,58,56,0.55)]"
          : "bg-ink text-canvas border-ink shadow-[4px_4px_0_0_rgba(5,5,5,1)] hover:shadow-[2px_2px_0_0_rgba(5,5,5,1)]",
      )}
    >
      <span className="shrink-0">{glyph}</span>
      <span className="flex flex-col leading-none text-left">
        <span className="text-[10px] uppercase tracking-wide opacity-80">
          {topLine}
        </span>
        <span className="text-base font-semibold">{bottomLine}</span>
      </span>
    </a>
  );
}

export interface AppDownloadProps {
  showQr?: boolean;
  showTrust?: boolean;
  align?: "left" | "center";
  className?: string;
  // Variant overrides (default to the production app links).
  appStoreUrl?: string;
  playStoreUrl?: string;
  qrHref?: string;
  qrSrc?: string;
  qrAlt?: string;
  qrCaption?: string;
  appStoreTopLine?: string;
  appStoreBottomLine?: string;
  playTopLine?: string;
  playBottomLine?: string;
  /** `light` for badges on a saturated ground. */
  tone?: "dark" | "light";
  /** Where on the site the badges sit (hero, footer, download...), sent with `store_badge_click`. */
  placement?: string;
}

const AppDownload: React.FC<AppDownloadProps> = ({
  showQr = false,
  showTrust = false,
  align = "left",
  className,
  appStoreUrl = APP_STORE_URL,
  playStoreUrl = PLAY_STORE_URL,
  qrHref = DOWNLOAD_URL,
  qrSrc = "/qr-download.svg",
  qrAlt = "QR code to download the LetsLoop app",
  qrCaption = "Scan to download",
  appStoreTopLine = "Download on the",
  appStoreBottomLine = "App Store",
  playTopLine = "Get it on",
  playBottomLine = "Google Play",
  tone = "dark",
  placement = "unspecified",
}) => {
  const alignItems =
    align === "center" ? "items-center text-center" : "items-start";
  // Narrowed rather than cast: this repo has no @types/react, so React.FC does
  // not constrain the destructured props and `tone` widens to string. The same
  // gap is why `key` on a local function component is rejected here (see
  // EventsPage / SupportPage). A comparison keeps it type-safe without an
  // `as` assertion that would hide a genuinely wrong value.
  const badgeTone = tone === "light" ? "light" : "dark";
  return (
    <div className={cn("flex flex-col gap-5", alignItems, className)}>
      <div
        className={cn(
          "flex flex-wrap gap-4",
          align === "center" ? "justify-center" : "justify-start",
          showQr && "sm:flex-nowrap",
        )}
      >
        {showQr && (
          <a
            href={qrHref}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 bg-canvas p-2.5 border-2 border-ink shadow-[6px_6px_0_0_rgba(5,5,5,1)]"
            aria-label={qrCaption}
          >
            <img
              src={qrSrc}
              alt={qrAlt}
              width={104}
              height={104}
              className="block"
              style={{ width: 104, height: 104 }}
            />
            <span className="mt-1 block text-center text-[11px] font-semibold uppercase tracking-wide text-ink/70">
              {qrCaption}
            </span>
          </a>
        )}
        <div className="flex flex-col gap-3 justify-center">
          <StoreBadge
            tone={badgeTone}
            placement={placement}
            href={appStoreUrl}
            glyph={<AppleGlyph />}
            topLine={appStoreTopLine}
            bottomLine={appStoreBottomLine}
          />
          <StoreBadge
            tone={badgeTone}
            placement={placement}
            href={playStoreUrl}
            glyph={<GooglePlayGlyph />}
            topLine={playTopLine}
            bottomLine={playBottomLine}
          />
        </div>
      </div>

      {showTrust && (
        <ul
          className={cn(
            "flex flex-wrap gap-x-3 gap-y-1 text-sm font-semibold text-ink/70",
            align === "center" ? "justify-center" : "justify-start",
          )}
        >
          {TRUST_TRIO.map((item, i) => (
            <li key={item} className="flex items-center gap-3">
              {i > 0 && (
                <span aria-hidden="true" className="text-ink/30">
                  ·
                </span>
              )}
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AppDownload;
